/**
 * Validates that a path is a safe "return to search" target for breadcrumb back-links.
 * Only allows relative paths that point to our SRP (search results page) to avoid open redirects.
 */

const SUPPORTED_LOCALES = ['en', 'fi', 'sv'] as const;
const JOBS_PATH_SEGMENTS: Record<(typeof SUPPORTED_LOCALES)[number], string[]> = {
  en: ['jobs'],
  fi: ['tyopaikat'],
  sv: ['lediga-jobb', 'jobb'],
};

/**
 * Returns true if the given path is a valid internal search/jobs path we can safely link to.
 * Path must be relative (e.g. /en/jobs, /fi/tyopaikat?search=dev) and match our SRP structure.
 */
export function isValidSearchReturnPath(path: string | null | undefined): boolean {
  if (path == null || typeof path !== 'string') {
    return false;
  }
  const trimmed = path.trim();
  if (trimmed.length === 0) {
    return false;
  }
  // Must be relative (no protocol or host)
  if (trimmed.includes('://') || trimmed.startsWith('//')) {
    return false;
  }
  // Normalize: ensure leading slash, strip hash
  const pathname = trimmed.split('#')[0].split('?')[0];
  const pathWithLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const segments = pathWithLeadingSlash.split('/').filter(Boolean);
  if (segments.length < 2) {
    return false;
  }
  const [locale, firstSegment] = segments;
  if (!locale || !SUPPORTED_LOCALES.includes(locale as (typeof SUPPORTED_LOCALES)[number])) {
    return false;
  }
  const allowedSegments = JOBS_PATH_SEGMENTS[locale as (typeof SUPPORTED_LOCALES)[number]];
  if (!allowedSegments?.includes(firstSegment)) {
    return false;
  }
  return true;
}
